<!--
  - Copyright (c) 2018 BarD Software s.r.o
  -->

<template>
    <div :id="`project-item-${project.refid}`" class="w-100 border-0">
        <div class="mx-0 row align-items-center">
            <div class="project-left align-items-baseline">
                <i class="scaling-icon-r hidden fa fa-lock lock-status"></i>
                <span>{{ project.name }}</span>
            </div>
            <div class="ml-1 dropleft">
                <button class="btn btn-outline-secondary btn-icon btn-menu"
                        data-toggle="dropdown"
                        aria-haspopup="true"
                        aria-expanded="false">
                    <i class="fas fa-ellipsis-v"></i>
                </button>
                <div class="dropdown-menu">
                    <a class="dropdown-item" href="#"
                            v-show="!lock"
                            data-target="#project-lock-modal"
                            data-toggle="modal">
                        <i class="fa fa-lock fa-fw"></i>&nbsp;Lock&hellip;
                    </a>
                    <a class="dropdown-item" href="#"
                            v-show="lock"
                            v-on:click="unlock">
                        <i class="fa fa-lock-open fa-fw"></i>&nbsp;Unlock
                    </a>
                    <a class="dropdown-item" href="#"
                            data-target="#project-remove-modal"
                            data-toggle="modal">
                        <i class="fa fa-trash fa-fw"></i>&nbsp;Delete&hellip;
                    </a>
                    <a class="dropdown-item" href="#"
                            v-on:click="toggleProjectHistory">
                        <i class="fa fa-clock fa-fw"></i>&nbsp;Version History
                    </a>
                </div>
            </div>
        </div>
        <div class="project-history-short-modal collapse">
            <div class="my-1">
                <div class="small project-history list-group">
                    <version-item
                            v-for="v in latestVersions.slice(0, 3)"
                            v-bind:key="v.number"
                            v-bind="{'data-version': v.number}"
                            v-bind:version="v"
                            v-bind:project="project"
                            v-bind:glasspane="toggleGlasspane">
                    </version-item>
                </div>
                <div v-bind:class="{ 'd-none': !showFullHistoryButton() }">
                    <div class="my-2"></div>
                    <button class="btn btn-outline-secondary btn-block btn-show-history"
                            v-on:click="showFullHistory">Full History</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import {Prop, Watch, Component, Vue} from 'vue-facing-decorator';
import TeamInfo from './TeamInfo.vue';
import {ProjectDto, VersionDto} from '@/Teams';
import {handleXhrError, SelectionModel} from '@/utils/Ui';
import ProjectHistoryMethods from './ProjectHistory';
import {ProjectUnlock, ProjectVersions} from '@/ProjectRoutes';
import FadingElement from '../utils/FadingElement';
import {Deferred} from 'jquery';

import $ from 'jquery';

const UPDATE_HISTORY_EVENT = 'projectitem.updatehistory';

export function updateHistory(refid: string) {
  $(`#project-item-${refid}`).trigger(UPDATE_HISTORY_EVENT);
}

const MAX_SHORT_HISTORY_SIZE = 3;

@Component
export default class ProjectItem extends Vue {
  private _lock: boolean = false;
  set lock(value: boolean) {
    const oldValue = this._lock;
    this._lock = value;
    this.onSetLock(value, oldValue);
  }
  get lock(): boolean {
    return this._lock;
  }

  @Prop()
  public project: ProjectDto;
  private currentVersion?: VersionDto;
  private versionSelectionModel?: SelectionModel<VersionDto>;
  private latestVersions: List<VersionDto> = [];
  private afterUpdates: MutableList<JQueryDeferred<any>> = [];

  private projectHistory: ProjectHistoryMethods;
  private readonly glasspane = new FadingElement('#team-info .glasspane');

  public updated() {
    this.$nextTick(() => {
      this.afterUpdates.map((deferred) => deferred.resolve());
      this.afterUpdates = [];
    });
  }

  public mounted() {
    // @ts-ignore
    this.projectHistory = this.$parent.$parent.$refs.projectFullHistory;

    this.onSetProject(this.project);

    $(this.$el).on(UPDATE_HISTORY_EVENT, () => this.updateHistory().then(() => {
      if (this.projectHistory.projectRefid() === this.project.refid) {
        this.projectHistory.updateHistory(this.project);
      }
    }));
  }

  public updateHistory(): JQueryPromise<any> {
    if (!this.versionSelectionModel) {
      const d = Deferred();
      d.reject();
      return d.promise();
    }

    return ProjectVersions.invoke({
      projectRefid: this.project.refid,
    })
        .then((versions: List<VersionDto>) => this.latestVersions = versions.slice(0, MAX_SHORT_HISTORY_SIZE + 1))
        .fail((jqXhr) => handleXhrError(jqXhr, 'can\'t download history'));
  }

  public unlock() {
    ProjectUnlock.invoke({ projectRefid: this.project.refid })
        .then(() => this.project.lock = undefined)
        .then(() => this.lock = false)
        .fail((jqXhr) => handleXhrError(jqXhr, 'can\'t unlock project'));
  }

  public toggleProjectHistory() {
    const el = $(this.$el).find('.project-history-short-modal');

    if (el.is(':visible')) {
      el.collapse('hide');
      return;
    }

    if (!this.versionSelectionModel) {
      this.versionSelectionModel = new SelectionModel<VersionDto>(
          `#project-item-${this.project.refid} .project-history-short-modal`,
          '.list-group-item',
          'data-version',
          (a: VersionDto, b: VersionDto) => a.number === b.number);
    }

    if (this.latestVersions.length > 0) {
      el.collapse('show');
      return;
    }

    this.updateHistory();

    this.afterUpdates.push(Deferred().done(() => el.collapse('show')));
  }

  public showFullHistory() {
    const teamInfo = this.$parent.$parent;
    if (teamInfo instanceof TeamInfo) {
      (teamInfo as TeamInfo).showFullHistory(this.project);
    }
  }

  @Watch("project")
  private onSetProject(project: ProjectDto) {
    this.lock = project.lock ? project.lock.expirationEpochTs >= Date.now() : false;
  }

  private onSetLock(newVal: boolean, oldVal: boolean) {
    if (!oldVal && newVal) {
      const el = $(this.$el).find('.lock-status');
      el.removeClass('hidden').addClass('scale-up');
      el.on('animationend', () => el.off('animationend').removeClass('scale-up'));
    }

    if (oldVal && !newVal) {
      const el = $(this.$el).find('.lock-status');
      el.addClass('scale-down');
      el.on('animationend', () => el.off('animationend').addClass('hidden').removeClass('scale-down'));
    }
  }

  public showFullHistoryButton(): boolean {
    return this.latestVersions.length > MAX_SHORT_HISTORY_SIZE;
  }

  @Watch('latestVersions')
  public watchLatestVersions(val) {
    const id2model = (id) => val.find((dto) => dto.number.toString() === id);
    const deferred = this.versionSelectionModel!.init(id2model, (version) => this.currentVersion = version);
    this.afterUpdates.push(deferred);
  }

  public toggleGlasspane(show: boolean) {
    this.glasspane.toggle(!show);
  }
}
</script>
